import { usePromptInputAttachments } from '@crowdex/ui/ai/prompt-input';
import { Button } from '@crowdex/ui/components/button';
import {
  DesktopDropdownContent,
  DesktopDropdownItem,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Switch } from '@crowdex/ui/components/switch';
import { toast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Box,
  Camera,
  Check,
  ChevronRight,
  Cloud,
  FileBox,
  FilePlus2,
  FileSearch,
  FileText,
  GitBranch,
  GitPullRequest,
  Image,
  LayoutGrid,
  ListChecks,
  MessageSquare,
  PanelsTopLeft,
  Paperclip,
  Plug,
  Rewind,
  Search,
  Sparkles,
  Terminal,
  UserRound,
  Users,
  Zap,
} from 'lucide-react';
import {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgentAvatar } from '@/components/agent-avatar';
import { PickerRow } from '@/components/desktop-shell/composer-menu-list';
import { ItemIcon } from '@/components/item-icon';
import { ArtifactGrid } from '@/features/apps/artifact-grid';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import {
  type AcpSelectConfigOption,
  currentModelName,
  currentModelOption,
  flattenConfigOptions,
} from '@/lib/desktop-config-options';
import type { ComposerCommand } from '@/lib/desktop-shell/composer-commands';
import { buildMcpServerMenuItems, type ModelChoice } from '@/lib/desktop-shell/composer-menu';
import { permissionName, permissionOption } from '@/lib/desktop-shell/composer-permissions';
import {
  highlightParts,
  type MentionFilter,
  mentionFilters,
  type PickerAction,
  type PickerItem,
  type PickerParticipant,
  type PickerScope,
  type PickerSection,
  type PickerSetting,
  type PickerSources,
  pickerItems,
  pickerSections,
} from '@/lib/desktop-shell/composer-picker';
import { pickFiles } from '@/lib/desktop-shell/pick-files';
import { recordRecentSkill, useRecentSkills } from '@/lib/desktop-shell/recent-skills';
import type { RepositoryFile } from '@/lib/desktop-shell/repository-files';
import {
  captureScreenshot,
  screenCaptureSupported,
  screenshotCancelled,
} from '@/lib/desktop-shell/screenshot';
import { useEnabledMcpServers } from '@/lib/desktop-shell/use-mcp-servers';
import { useMentionLibrary } from '@/lib/desktop-shell/use-mention-library';
import { errorMessage } from '@/lib/error-message';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import { useRouter } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import { type MentionTarget, mentionArtifact } from '@/lib/side-panel/mention-targets';
import { useComposerCommands } from './composer-commands-context';

/**
 * The conversation settings the + reaches: Plan mode on the desktop (D7); on a phone (mobile 5,
 * without the desktop's chips) where it runs and its permissions too. The agent is the model
 * popover's first choice.
 */
export type ComposerPlusSettings = {
  /** Where it runs ("Cloud sandbox", "This computer") and its repository's setup. */
  environment?: { label: string; onOpen: (() => void) | undefined } | undefined;
  /** The agent's plan mode, when it has one. */
  planFirst?: { enabled: boolean; onChange: (enabled: boolean) => void } | undefined;
};

/** What the picker adds at once: references (artifacts among them) and files of the repository. */
type ReferenceSelection = {
  targets: readonly MentionTarget[];
  files: readonly RepositoryFile[];
};

/** Where a row of the + leads: / for the skills, @ for the references (files, artifacts first). */
export type PickerOpenScope = 'skills' | 'mention' | 'files' | 'artifacts';

export type ComposerPickerHandle = {
  /** A key typed in the composer while the picker shows: `true` when the picker took it. */
  handleKeyDown: (event: PickerKeyEvent) => boolean;
};

type PickerKeyEvent = Pick<KeyboardEvent, 'key' | 'shiftKey' | 'metaKey' | 'ctrlKey' | 'altKey'> & {
  isComposing?: boolean;
  preventDefault: () => void;
};

export type ComposerPickerProps = {
  scope: PickerScope;
  /** What is typed: after / or @ in the composer, or into the + itself. */
  query: string;
  skills: readonly AvailablePluginSkill[];
  targets: readonly MentionTarget[];
  /** The repository's files matching the query. */
  files?: readonly RepositoryFile[];
  /** Crowdex's commands (/ only). */
  commands?: readonly ComposerCommand[];
  /** A group conversation's participants, whom @ notifies. */
  participants?: readonly PickerParticipant[];
  /** The conversation's settings (its model and permissions among them). */
  modelOptions?: readonly AcpSelectConfigOption[];
  /** The rows of the + without a query. */
  actions?: readonly PickerAction[];
  settings?: ComposerPlusSettings;
  /** The + types into the picker itself (type-ahead); / and @ are typed in the composer. */
  typeAhead?: { onChange: (query: string) => void };
  /** The @ filter it opens on ("file", "artifact"). */
  initialFilter?: string;
  /** A phone's sheet: taller rows, no keys. */
  touch?: boolean;
  onSkill: (skill: AvailablePluginSkill) => void;
  onMention: (target: MentionTarget) => void;
  onReferences: (selection: ReferenceSelection) => void;
  onParticipant?: (participant: PickerParticipant) => void;
  onChangeModel?: (configId: string, value: string) => void;
  /** Mention a file (@), All skills (/): the picker scoped. */
  onOpenScope?: (scope: PickerOpenScope) => void;
  /** Backspace on an empty query: back to the whole list. */
  onUnscope?: () => void;
  /** Esc. */
  onDismiss: () => void;
  /** Any row chosen (after it acted). */
  onPicked?: (item: PickerItem) => void;
  /** Gives the keyboard back to where the user types (after a submenu closed). */
  onFocusHost?: () => void;
};

const COMMAND_ICONS: Record<ComposerCommand['id'], typeof Rewind> = {
  rewind: Rewind,
  branch: GitBranch,
  review: FilePlus2,
  pr: GitPullRequest,
};
const ACTION_ICONS: Record<PickerAction, typeof Paperclip> = {
  files: Paperclip,
  screenshot: Image,
  'mention-file': FileSearch,
  camera: Camera,
  photos: Image,
};
const SETTING_ICONS: Record<PickerSetting, typeof ListChecks> = {
  plan: ListChecks,
  permissions: Zap,
  environment: Cloud,
  model: Box,
  mcp: Plug,
};
const MENTION_ICONS: Record<MentionTarget['kind'], typeof FileText> = {
  chat: MessageSquare,
  persona: UserRound,
  artifact: FileBox,
  tab: PanelsTopLeft,
  app: LayoutGrid,
  terminal: Terminal,
};
const SCOPE_LABEL: Record<Exclude<PickerScope, 'all'>, string> = {
  skills: 'Skills',
  mention: 'Mention',
};
const ICON = 'size-4 shrink-0 text-muted-foreground';
/** What the picker keeps from the window's edge, and the least its list shows. */
const WINDOW_MARGIN = 8;
const MIN_LIST_HEIGHT = 96;

/** The label of a row, its match in bold. */
function Highlighted({ text, query }: { text: string; query: string }) {
  const parts = highlightParts(text, query);
  if (parts === null) return text;
  return (
    <>
      {parts.before}
      <strong className="font-semibold text-foreground">{parts.match}</strong>
      {parts.after}
    </>
  );
}

/** A key the row answers to, as a small chip ("@", "/"). */
function KeyChip({ children }: { children: ReactNode }) {
  return (
    <kbd className="type-caption flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-hairline-strong px-1 font-sans text-muted-foreground">
      {children}
    </kbd>
  );
}

/**
 * The composer's one picker (D7, D7b, D7c): the + without a query (what to add, the recent
 * skills, the settings), / (the commands and skills) and @ (the references, filtered by pills,
 * artifacts as thumbnails, Space to select several). The same rows, keys and empty state
 * everywhere: ↑ ↓ move, ↵ inserts, Esc closes, Backspace on an empty query leaves a scope.
 */
export const ComposerPicker = forwardRef<ComposerPickerHandle, ComposerPickerProps>(
  function ComposerPicker(props, ref) {
    const {
      scope,
      query,
      skills,
      targets: conversationTargets,
      files = [],
      commands,
      participants,
      modelOptions = [],
      actions,
      settings = {},
      typeAhead,
      initialFilter,
      touch = false,
      onSkill,
      onMention,
      onReferences,
      onParticipant,
      onChangeModel,
      onOpenScope,
      onUnscope,
      onDismiss,
      onPicked,
      onFocusHost,
    } = props;
    // The library's artifacts and the installed apps join the conversation's references once
    // they are listed: @, or a search of the +.
    const targets = useMentionLibrary(
      conversationTargets,
      scope === 'mention' || (scope === 'all' && query.trim().length > 0),
    );
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const attachments = usePromptInputAttachments();
    const runner = useComposerCommands();
    const recentSkillIds = useRecentSkills();
    const workbench = useWorkbench();
    const router = useRouter();
    const mcpServers = useEnabledMcpServers();
    const mcpItems = useMemo(() => buildMcpServerMenuItems(mcpServers), [mcpServers]);
    const model = currentModelOption(modelOptions);
    const permissions = permissionOption(modelOptions);
    const modelChoices = useMemo<ModelChoice[]>(
      () =>
        model === undefined
          ? []
          : flattenConfigOptions(model).map((choice) => ({
              configId: model.id,
              value: choice.value,
              name: choice.name,
            })),
      [model],
    );
    const [filterId, setFilterId] = useState(initialFilter ?? 'all');
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [openMenu, setOpenMenu] = useState<PickerSetting | null>(null);

    const settingRows = useMemo<PickerSetting[]>(
      () => [
        ...(touch && permissions !== undefined ? (['permissions'] as const) : []),
        ...(touch && settings.environment !== undefined ? (['environment'] as const) : []),
        ...(settings.planFirst !== undefined ? (['plan'] as const) : []),
      ],
      [permissions, settings.environment, settings.planFirst, touch],
    );
    const filters =
      scope === 'mention' ? mentionFilters(targets, files, filterId) : ([] as MentionFilter[]);
    const filter = filters.find((candidate) => candidate.id === filterId) ?? filters[0];
    const sources: PickerSources = {
      ...(commands === undefined ? {} : { commands }),
      skills,
      recentSkillIds,
      targets,
      files,
      ...(participants === undefined ? {} : { participants }),
      modelChoices,
      modelName: currentModelName(modelOptions),
      ...(actions === undefined ? {} : { actions }),
      settings: settingRows,
      mcp: scope !== 'mention' && participants === undefined,
    };
    const sections = pickerSections({ scope, query, sources, filter });
    const items = pickerItems(sections);
    const active = items[Math.min(activeIndex, items.length - 1)];
    const selected = items.filter((item) => selectedKeys.has(item.key));

    useEffect(() => {
      if (filterId !== 'all' && !filters.some((candidate) => candidate.id === filterId))
        setFilterId('all');
    });
    // Another list (its scope, query or filter changed) starts at its first item.
    const listKey = `${scope}\0${query}\0${filterId}`;
    const [listedKey, setListedKey] = useState(listKey);
    if (listedKey !== listKey) {
      setListedKey(listKey);
      setActiveIndex(0);
    }
    // The list never runs off the window: above a composer high on the screen (a new
    // conversation's), it is as tall as the room above it leaves.
    const [listRoom, setListRoom] = useState<number | null>(null);
    useLayoutEffect(() => {
      if (touch) return;
      const measure = () => {
        const list = listRef.current;
        const panel = list?.closest<HTMLElement>('[data-composer-menu]');
        if (list === null || list === undefined || panel === null || panel === undefined) return;
        const rect = panel.getBoundingClientRect();
        if (rect.height === 0) return;
        const room =
          panel.dataset['placement'] === 'below'
            ? window.innerHeight - rect.top - WINDOW_MARGIN
            : rect.bottom - WINDOW_MARGIN;
        const chrome = rect.height - list.getBoundingClientRect().height;
        setListRoom(Math.max(MIN_LIST_HEIGHT, Math.floor(room - chrome)));
      };
      measure();
      // Its header grows as pills wrap (the library read), its footer with "Add N".
      const panel = listRef.current?.closest<HTMLElement>('[data-composer-menu]');
      const observer =
        typeof ResizeObserver === 'undefined' || panel === null || panel === undefined
          ? null
          : new ResizeObserver(measure);
      if (panel !== null && panel !== undefined) observer?.observe(panel);
      window.addEventListener('resize', measure);
      return () => {
        observer?.disconnect();
        window.removeEventListener('resize', measure);
      };
    }, [touch]);
    const typing = typeAhead !== undefined;
    useEffect(() => {
      if (typing && !touch) inputRef.current?.focus();
    }, [typing, touch]);
    useEffect(() => {
      if (active === undefined) return;
      const artifactId = active.type === 'mention' ? mentionArtifact(active.target)?.id : undefined;
      [
        ...(rootRef.current?.querySelectorAll<HTMLElement>(
          '[data-picker-key], [data-artifact-key]',
        ) ?? []),
      ]
        .find(
          (element) =>
            element.dataset['pickerKey'] === active.key ||
            (artifactId !== undefined && element.dataset['artifactKey'] === artifactId),
        )
        ?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    function picked(item: PickerItem): void {
      onPicked?.(item);
    }

    function runAction(action: PickerAction, item: PickerItem): void {
      if (action === 'mention-file') {
        onOpenScope?.('files');
        return;
      }
      picked(item);
      if (action === 'files') requestAnimationFrame(() => attachments.openFileDialog());
      else if (action === 'camera' || action === 'photos')
        pickFiles(action, (chosen) => attachments.add(chosen));
      else
        void captureScreenshot()
          .then((file) => attachments.add([file]))
          .catch((reason: unknown) => {
            // The user cancelling the share is no failure; anything else says why.
            if (screenshotCancelled(reason)) return;
            toast.error('Couldn’t take a screenshot', errorMessage(reason));
          });
    }

    function activate(item: PickerItem): void {
      switch (item.type) {
        case 'action':
          runAction(item.action, item);
          return;
        case 'command':
          if (runner?.available(item.command.id) !== true) return;
          picked(item);
          runner.run(item.command.id);
          return;
        case 'skill':
          recordRecentSkill(item.skill.uri);
          onSkill(item.skill);
          picked(item);
          return;
        case 'file':
          onReferences({ targets: [], files: [item.file] });
          picked(item);
          return;
        case 'mention':
          if (item.target.kind === 'artifact') onReferences({ targets: [item.target], files: [] });
          else onMention(item.target);
          picked(item);
          return;
        case 'participant':
          onParticipant?.(item.participant);
          picked(item);
          return;
        case 'model-choice':
          onChangeModel?.(item.choice.configId, item.choice.value);
          picked(item);
          return;
        case 'setting':
          if (item.setting === 'plan') {
            settings.planFirst?.onChange(!settings.planFirst.enabled);
            return;
          }
          if (item.setting === 'environment') {
            picked(item);
            settings.environment?.onOpen?.();
            return;
          }
          setOpenMenu(item.setting);
          return;
      }
    }

    function toggle(item: PickerItem): void {
      setSelectedKeys((current) => {
        const next = new Set(current);
        if (next.has(item.key)) next.delete(item.key);
        else next.add(item.key);
        return next;
      });
    }

    function confirmSelection(): void {
      onReferences({
        targets: selected.flatMap((item) => (item.type === 'mention' ? [item.target] : [])),
        files: selected.flatMap((item) => (item.type === 'file' ? [item.file] : [])),
      });
      const first = selected[0];
      if (first !== undefined) picked(first);
    }

    function handleKey(event: PickerKeyEvent): boolean {
      if (event.isComposing) return false;
      const { key } = event;
      if (key === 'Escape') {
        event.preventDefault();
        if (typeAhead !== undefined && query.length > 0) typeAhead.onChange('');
        else onDismiss();
        return true;
      }
      if (key === 'Backspace' && query.length === 0 && scope !== 'all' && onUnscope) {
        event.preventDefault();
        onUnscope();
        return true;
      }
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        if (items.length === 0) return false;
        event.preventDefault();
        const delta = key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((current) => (current + delta + items.length) % items.length);
        return true;
      }
      if (
        scope === 'mention' &&
        (key === 'ArrowLeft' || key === 'ArrowRight') &&
        filters.length > 1
      ) {
        event.preventDefault();
        const current = Math.max(
          0,
          filters.findIndex((candidate) => candidate.id === filter?.id),
        );
        const delta = key === 'ArrowRight' ? 1 : -1;
        setFilterId(filters[(current + delta + filters.length) % filters.length]?.id ?? 'all');
        return true;
      }
      if (
        key === ' ' &&
        scope === 'mention' &&
        active !== undefined &&
        active.type !== 'participant'
      ) {
        event.preventDefault();
        toggle(active);
        return true;
      }
      if ((key === 'Enter' || key === 'Tab') && !event.shiftKey) {
        if (selected.length > 0) {
          event.preventDefault();
          confirmSelection();
          return true;
        }
        if (active === undefined) return false;
        event.preventDefault();
        activate(active);
        return true;
      }
      return false;
    }

    useImperativeHandle(ref, () => ({ handleKeyDown: handleKey }));

    /** The icon of an app's tab (its own, painted by the core), else the apps' glyph. */
    function appIcon(plugin: string, fallback: ReactNode = <LayoutGrid className={ICON} />) {
      const panel = workbench.panels.find((candidate) => candidate.plugin === plugin);
      return panel === undefined ? (
        fallback
      ) : (
        <span className="flex size-4 shrink-0 items-center justify-center">
          <ItemIcon
            className="size-3.5"
            icon={pluginIcon(panel.icon ?? 'app')}
            src={panel.iconSrc}
          />
        </span>
      );
    }

    function skillIcon(skill: AvailablePluginSkill): ReactNode {
      // A skill shows the icon of its plugin's app, when it brings one.
      return appIcon(skill.pluginName, <Sparkles className={ICON} strokeWidth={1.8} />);
    }

    function settingValue(setting: PickerSetting): string | undefined {
      if (setting === 'permissions') return permissions ? permissionName(permissions) : undefined;
      if (setting === 'environment') return settings.environment?.label;
      if (setting === 'model') return currentModelName(modelOptions) ?? undefined;
      return undefined;
    }

    /** The submenu a setting opens, beside its row. */
    function settingMenu(setting: PickerSetting): ReactNode {
      const option =
        setting === 'permissions' ? permissions : setting === 'model' ? model : undefined;
      if (option !== undefined)
        return (
          <>
            <DesktopDropdownLabel className="py-1 font-normal">
              {setting === 'model' ? 'Model' : 'Permissions'}
            </DesktopDropdownLabel>
            <DropdownMenuRadioGroup
              onValueChange={(value) => {
                onChangeModel?.(option.id, value);
                setOpenMenu(null);
                picked({ key: `setting:${setting}`, type: 'setting', setting, label: setting });
              }}
              value={option.currentValue}
            >
              {flattenConfigOptions(option).map((choice) => (
                <DesktopDropdownRadioItem
                  key={choice.value}
                  title={choice.description}
                  value={choice.value}
                >
                  {/* A finger has no tooltip: a phone's sheet says what each choice does. */}
                  {touch && choice.description ? (
                    <span className="flex min-w-0 flex-col py-0.5">
                      <span>{choice.name}</span>
                      <span className="type-caption text-muted-foreground">
                        {choice.description}
                      </span>
                    </span>
                  ) : (
                    choice.name
                  )}
                </DesktopDropdownRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        );
      if (setting === 'mcp')
        return (
          <>
            {mcpItems.length > 0 && (
              <>
                <DesktopDropdownLabel className="py-1 font-normal">
                  MCP servers
                </DesktopDropdownLabel>
                {mcpItems.map((server) => (
                  <DesktopDropdownItem
                    key={server.name}
                    onSelect={() => router.push(pluginsPath('installed'))}
                  >
                    <Plug className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{server.name}</span>
                    <span className="ml-auto max-w-40 truncate text-muted-foreground">
                      {server.detail}
                    </span>
                  </DesktopDropdownItem>
                ))}
                <DropdownMenuSeparator className="my-0.5 bg-border/70" />
              </>
            )}
            <DesktopDropdownItem onSelect={() => router.push(pluginsPath())}>
              Manage plugins…
            </DesktopDropdownItem>
          </>
        );
      return null;
    }

    function renderItem(item: PickerItem, index: number): ReactNode {
      const isActive = active?.key === item.key;
      const isSelected = selectedKeys.has(item.key);
      const common = {
        active: isActive,
        'data-picker-key': item.key,
        onMouseEnter: () => setActiveIndex(index),
        touch,
      };
      const check = isSelected ? (
        <span className="flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="size-3" />
        </span>
      ) : null;
      const label = <Highlighted query={query} text={item.label} />;
      switch (item.type) {
        case 'action': {
          const Icon = ACTION_ICONS[item.action];
          return (
            <PickerRow
              {...common}
              disabled={item.action === 'screenshot' && !screenCaptureSupported()}
              icon={<Icon className={ICON} />}
              key={item.key}
              // A phone's Camera and Photos stand beside it: its own files.
              label={touch && item.action === 'files' ? 'Files' : label}
              onClick={() => activate(item)}
              trailing={item.action === 'mention-file' && !touch ? <KeyChip>@</KeyChip> : undefined}
            />
          );
        }
        case 'command': {
          const Icon = COMMAND_ICONS[item.command.id];
          return (
            <PickerRow
              {...common}
              description={item.command.description}
              disabled={runner?.available(item.command.id) !== true}
              icon={<Icon className={ICON} />}
              key={item.key}
              label={label}
              onClick={() => activate(item)}
            />
          );
        }
        case 'skill':
          return (
            <PickerRow
              {...common}
              description={item.skill.shortDescription ?? item.skill.description}
              icon={skillIcon(item.skill)}
              key={item.key}
              label={label}
              onClick={() => activate(item)}
              trailing={
                scope !== 'all' && item.skill.pluginDisplayName.length > 0 ? (
                  <span className="type-meta max-w-28 truncate text-muted-foreground">
                    {item.skill.pluginDisplayName}
                  </span>
                ) : undefined
              }
            />
          );
        case 'file':
          return (
            <PickerRow
              {...common}
              aria-label={
                item.file.directory === ''
                  ? item.file.name
                  : `${item.file.name} · ${item.file.directory}`
              }
              description={item.file.directory}
              icon={<FileText className={ICON} strokeWidth={1.8} />}
              key={item.key}
              label={label}
              mono
              onClick={() => activate(item)}
              trailing={
                <>
                  {item.file.status !== undefined && (
                    <span className="type-caption w-3 text-center font-mono text-muted-foreground">
                      {item.file.status}
                    </span>
                  )}
                  {check}
                </>
              }
            />
          );
        case 'mention': {
          const Icon = MENTION_ICONS[item.target.kind];
          return (
            <PickerRow
              {...common}
              // "Developer · Claude Code", not its two spans run together.
              aria-label={
                item.target.detail === '' ? item.label : `${item.label} · ${item.target.detail}`
              }
              description={item.target.detail}
              icon={
                item.target.kind === 'app' ? (
                  appIcon(item.target.id)
                ) : (
                  <Icon className={ICON} strokeWidth={1.8} />
                )
              }
              key={item.key}
              label={label}
              onClick={() => activate(item)}
              trailing={check}
            />
          );
        }
        case 'participant': {
          const participant = item.participant;
          return (
            <PickerRow
              {...common}
              icon={
                participant.id === '__all' ? (
                  <Users className="size-4 shrink-0" />
                ) : (
                  <AgentAvatar
                    agentId={participant.agentId}
                    avatar={participant.avatar as ComponentProps<typeof AgentAvatar>['avatar']}
                    className="size-4 shrink-0"
                    name={participant.name}
                  />
                )
              }
              key={item.key}
              label={label}
              onClick={() => activate(item)}
            />
          );
        }
        case 'model-choice':
          return (
            <PickerRow
              {...common}
              icon={<Box className={ICON} />}
              key={item.key}
              label={label}
              onClick={() => activate(item)}
            />
          );
        case 'setting': {
          const Icon = SETTING_ICONS[item.setting];
          if (item.setting === 'plan' && settings.planFirst !== undefined) {
            const plan = settings.planFirst;
            return (
              // A row of its own: the switch is the control, the whole row toggles it.
              // biome-ignore lint/a11y/noStaticElementInteractions: the row widens the switch's target; the switch is the control.
              // biome-ignore lint/a11y/useKeyWithClickEvents: the switch inside (and ↵ from the picker) answers the keyboard.
              <div
                className={cn(
                  'type-ui flex w-full cursor-default items-center gap-2.5 rounded-md px-2 text-foreground transition-colors',
                  touch ? 'min-h-11' : 'h-8',
                  isActive ? 'bg-accent' : 'hover:bg-accent/60',
                )}
                data-active={isActive || undefined}
                data-picker-key={item.key}
                key={item.key}
                onClick={(event) => {
                  if (event.target instanceof Element && event.target.closest('[role="switch"]'))
                    return;
                  activate(item);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <Icon className={ICON} />
                <span className="shrink-0">{label}</span>
                {touch && (
                  <span className="type-meta min-w-0 flex-1 truncate text-muted-foreground">
                    Plans first, edits after you approve
                  </span>
                )}
                <Switch
                  aria-label="Plan mode"
                  checked={plan.enabled}
                  className="ml-auto shrink-0"
                  onCheckedChange={plan.onChange}
                />
              </div>
            );
          }
          const value = settingValue(item.setting);
          const row = (
            <PickerRow
              {...common}
              icon={<Icon className={ICON} />}
              key={item.key}
              label={label}
              onClick={item.setting === 'environment' ? () => activate(item) : undefined}
              trailing={
                <>
                  {value !== undefined && (
                    <span className="type-meta max-w-36 truncate text-muted-foreground">
                      {value}
                    </span>
                  )}
                  <ChevronRight className="size-4 text-muted-foreground" />
                </>
              }
            />
          );
          if (item.setting === 'environment') return row;
          return (
            <DropdownMenu
              key={item.key}
              modal={false}
              onOpenChange={(open) => setOpenMenu(open ? item.setting : null)}
              open={openMenu === item.setting}
            >
              <DropdownMenuTrigger asChild>{row}</DropdownMenuTrigger>
              <DesktopDropdownContent
                align={touch ? 'start' : 'end'}
                aria-label={item.label}
                className="min-w-52 max-w-[calc(100vw-1.5rem)]"
                collisionPadding={12}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  if (typeAhead !== undefined) inputRef.current?.focus();
                  else onFocusHost?.();
                }}
                side={touch ? 'top' : 'right'}
                sideOffset={8}
              >
                {settingMenu(item.setting)}
              </DesktopDropdownContent>
            </DropdownMenu>
          );
        }
      }
    }

    function renderSection(section: PickerSection, offset: number): ReactNode {
      const heading =
        section.id === 'recent-skills' ? (
          <div className="flex items-center px-2 pt-1.5 pb-0.5">
            <h3 className="type-caption text-muted-foreground">{section.label}</h3>
            {onOpenScope !== undefined && (
              <button
                className="type-caption ml-auto flex items-center gap-1.5 rounded px-1 text-muted-foreground hover:text-foreground"
                onClick={() => onOpenScope('skills')}
                onMouseDown={(event) => event.preventDefault()}
                type="button"
              >
                All skills
                {!touch && <KeyChip>/</KeyChip>}
              </button>
            )}
          </div>
        ) : section.label !== null && !(scope === 'mention' && filter?.kind !== undefined) ? (
          <h3 className="type-caption px-2 pt-1.5 pb-0.5 text-muted-foreground">
            {section.id === 'artifact' && scope === 'mention'
              ? `${section.label} · ${section.items.length}`
              : section.label}
          </h3>
        ) : null;
      const body =
        section.id === 'artifact' && scope === 'mention' ? (
          <ArtifactGrid
            artifacts={section.items.flatMap((item) => {
              const artifact = item.type === 'mention' ? mentionArtifact(item.target) : null;
              return artifact ? [artifact] : [];
            })}
            className="px-1 py-1"
            selection={{
              focusedId:
                active?.type === 'mention' ? mentionArtifact(active.target)?.id : undefined,
              selectedIds: new Set(
                selected.flatMap((item) => {
                  const artifact = item.type === 'mention' ? mentionArtifact(item.target) : null;
                  return artifact ? [artifact.id] : [];
                }),
              ),
              onFocus: (artifact) =>
                setActiveIndex(
                  items.findIndex(
                    (item) =>
                      item.type === 'mention' && mentionArtifact(item.target)?.id === artifact.id,
                  ),
                ),
              onToggle: (artifact) => {
                const item = section.items.find(
                  (candidate) =>
                    candidate.type === 'mention' &&
                    mentionArtifact(candidate.target)?.id === artifact.id,
                );
                if (item) toggle(item);
              },
            }}
            size="compact"
          />
        ) : (
          section.items.map((item, index) => renderItem(item, offset + index))
        );
      return (
        <section
          aria-label={section.label ?? undefined}
          className={cn(
            'flex flex-col',
            // The + without a query: its groups apart by a hairline (option A).
            offset > 0 &&
              scope === 'all' &&
              query.length === 0 &&
              'mt-1 border-hairline border-t pt-1',
          )}
          data-picker-section={section.id}
          key={section.id}
        >
          {heading}
          {body}
        </section>
      );
    }

    let offset = 0;
    const body = sections.map((section) => {
      const node = renderSection(section, offset);
      offset += section.items.length;
      return node;
    });
    const keys =
      touch || (scope === 'all' && query.length === 0)
        ? null
        : [
            ...(scope === 'mention' && filters.length > 1 ? ['← → filter'] : []),
            '↑↓ move',
            ...(scope === 'mention' ? ['Space select'] : []),
            '↵ insert',
            ...(scope === 'all' || (query.length === 0 && onUnscope !== undefined)
              ? ['Backspace back to the list']
              : ['Esc close']),
          ];

    return (
      <div className="flex min-h-0 flex-1 flex-col" data-picker-scope={scope} ref={rootRef}>
        {typeAhead !== undefined && (
          <div
            className={cn(
              'flex h-9 shrink-0 items-center gap-2 px-3',
              query.length === 0 && 'sr-only',
            )}
          >
            <Search className="size-3.5 shrink-0 text-muted-foreground" />
            <input
              aria-label="Search skills, files, conversations"
              className="type-ui h-full min-w-0 flex-1 bg-transparent outline-none"
              onChange={(event) => {
                const next = event.target.value;
                // / and @ typed first are the composer's: the picker scoped there.
                if (next === '/' || next === '@') {
                  typeAhead.onChange('');
                  onOpenScope?.(next === '/' ? 'skills' : 'mention');
                  return;
                }
                typeAhead.onChange(next);
              }}
              onKeyDown={(event) => {
                handleKey(event);
              }}
              ref={inputRef}
              value={query}
            />
            <span className="type-caption shrink-0 text-muted-foreground pointer-coarse:hidden">
              Esc clears
            </span>
          </div>
        )}
        {scope !== 'all' && (
          <div className="flex shrink-0 flex-col gap-1 px-1 pt-1">
            <div className="flex h-6 items-center gap-2 px-2">
              <span
                className="type-caption font-medium text-muted-foreground"
                data-picker-scope-label
              >
                {SCOPE_LABEL[scope]}
              </span>
              {scope === 'mention' && targets.some((target) => target.kind === 'artifact') && (
                <button
                  className="type-caption ml-auto text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/artifacts')}
                  onMouseDown={(event) => event.preventDefault()}
                  type="button"
                >
                  Open library →
                </button>
              )}
            </div>
            {filters.length > 1 && (
              <div className="flex shrink-0 flex-wrap gap-1 px-1 pb-1">
                {filters.map((candidate) => (
                  <button
                    aria-pressed={candidate.id === filter?.id}
                    className="type-meta flex h-7 shrink-0 items-center gap-1.5 rounded-control border border-transparent px-3 text-muted-foreground hover:bg-control hover:text-foreground aria-pressed:border-input aria-pressed:bg-control aria-pressed:font-semibold aria-pressed:text-foreground"
                    data-picker-filter={candidate.id}
                    key={candidate.id}
                    onClick={() => {
                      setFilterId(candidate.id);
                      onFocusHost?.();
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    type="button"
                  >
                    {candidate.label}
                    {candidate.id !== 'all' && (
                      <span className="tabular-nums" data-filter-count="">
                        {
                          pickerItems(
                            pickerSections({ scope, query, sources, filter: candidate }),
                          ).filter((item) => item.type !== 'participant').length
                        }
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain p-1',
            touch ? 'max-h-none' : 'max-h-[min(24rem,calc(100dvh-14rem))]',
          )}
          ref={listRef}
          style={
            listRoom === null || touch ? undefined : { maxHeight: `min(24rem, ${listRoom}px)` }
          }
        >
          {body}
          {items.length === 0 && (
            <p className="type-meta px-2 py-3 text-muted-foreground">No matching results</p>
          )}
        </div>
        {(keys !== null || selected.length > 0) && (
          <div
            className={cn(
              'type-caption flex min-h-8 shrink-0 flex-wrap items-center gap-x-3 px-3 pb-1.5 text-muted-foreground',
              // A touch screen has no keys to list: the line shows for "Add N" only.
              selected.length === 0 && 'pointer-coarse:hidden',
            )}
          >
            {keys?.map((key) => (
              <span className="pointer-coarse:hidden" key={key}>
                {key}
              </span>
            ))}
            {selected.length > 0 && (
              <Button
                className="type-caption ml-auto h-7 rounded-control px-3"
                onClick={confirmSelection}
                onMouseDown={(event) => event.preventDefault()}
                size="sm"
                type="button"
              >
                Add {selected.length}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  },
);
